import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, DatePicker, Modal, Popconfirm, Table, Tooltip } from "antd";
import { LoginOutlined, PlayCircleOutlined, ReloadOutlined, StopOutlined, VideoCameraOutlined } from "@ant-design/icons";
import axios from "axios";
import Utils from "../../misc/Utils";
import Enums from "../../misc/Enums";

const { RangePicker } = DatePicker;

export default function BraineeLiveClassPicker(props) {
   const [loading, setLoading] = useState(true);
   const [refreshing, setRefreshing] = useState(false);
   const [liveClassList, setLiveClassList] = useState([]);
   const [recordingsModalOpen, setRecordingsModalOpen] = useState(false);
   const [recordingsLoading, setRecordingsLoading] = useState(false);
   const [recordings, setRecordings] = useState([]);
   const [recordingDateFilter, setRecordingDateFilter] = useState(null);
   const [selectedMeetingName, setSelectedMeetingName] = useState("");
   const [recordingLookup, setRecordingLookup] = useState({});
   const [meetingStatusLookup, setMeetingStatusLookup] = useState({});
   const [attendeesLookup, setAttendeesLookup] = useState({});
   const [endingMeetingKey, setEndingMeetingKey] = useState("");
   const [hoveredMeetingKey, setHoveredMeetingKey] = useState("");
   const [isCtrlPressed, setIsCtrlPressed] = useState(false);
   const pickerListRequestInFlight = useRef(false);
   const pickerRefreshRequestInFlight = useRef(false);

   const username = props.username || Utils.getCurrentUserInfo(Enums.UserInfo.fullname);
   const userRole = Utils.getCurrentUserInfo(Enums.UserInfo.role);
   const canManagePickerList = userRole === "Super Admin";
   const joinAs = props.joinAs || (userRole === "Student" ? "attendee" : "moderator");

   useEffect(() => {
      Utils.verifyJwt();
      getLiveClassPickerList();
   }, []);

   useEffect(() => {
      const handleKeyDown = (event) => {
         if (event.key === "Control") {
            setIsCtrlPressed(true);
         }
      };

      const handleKeyUp = (event) => {
         if (event.key === "Control") {
            setIsCtrlPressed(false);
         }
      };

      const handleWindowBlur = () => {
         setIsCtrlPressed(false);
      };

      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keyup", handleKeyUp);
      window.addEventListener("blur", handleWindowBlur);

      return () => {
         window.removeEventListener("keydown", handleKeyDown);
         window.removeEventListener("keyup", handleKeyUp);
         window.removeEventListener("blur", handleWindowBlur);
      };
   }, []);

   const getSubdomain = () => {
      var host = window.location.host
      var subdomain = host.split('.')[0];
      if (subdomain.indexOf("localhost") !== -1)
         subdomain = "localhost";

      return subdomain;
   };

   const getRoomName = (payload) => `${payload.grade_level_name || ""} - ${payload.section_name || ""}`.trim();
   const getGradeLevelId = (payload) => payload?.grade_level_id ?? payload?.gradeLevelId ?? payload?.gradeLevelID ?? payload?.level_id ?? "";
   const getSectionId = (payload) => payload?.section_id ?? payload?.sectionId ?? payload?.sectionID ?? "";
   const getRecordingLookupKey = (payload) => {
      const gradeLevelId = getGradeLevelId(payload);
      const sectionId = getSectionId(payload);

      return gradeLevelId && sectionId ? `${gradeLevelId}::${sectionId}` : "";
   };
   const getLiveClassKey = (payload) => getRecordingLookupKey(payload);

   const getXmlNodeText = (node, tagName) => node.getElementsByTagName(tagName)?.[0]?.textContent || "";

   const getPlaybackUrl = (recording) => {
      const formats = recording?.playback?.format;

      if (Array.isArray(formats)) {
         return formats.find((format) => format?.url)?.url || "";
      }

      return formats?.url || recording?.playbackUrl || "";
   };

   const normalizeRecordingsData = (data) => {
      if (typeof data === "string") {
         const parser = new DOMParser();
         const xml = parser.parseFromString(data, "text/xml");
         const parserError = xml.getElementsByTagName("parsererror")?.[0];

         if (parserError) {
            return [];
         }

         return Array.from(xml.getElementsByTagName("recording")).map((recording) => {
            const format = recording.getElementsByTagName("format")?.[0];

            return {
               recordID: getXmlNodeText(recording, "recordID"),
               meetingID: getXmlNodeText(recording, "meetingID"),
               name: getXmlNodeText(recording, "name"),
               published: getXmlNodeText(recording, "published"),
               state: getXmlNodeText(recording, "state"),
               startTime: getXmlNodeText(recording, "startTime"),
               endTime: getXmlNodeText(recording, "endTime"),
               playbackUrl: format ? getXmlNodeText(format, "url") : "",
            };
         });
      }

      const responseData = data?.response ?? data?.data ?? data;
      const rawRecordings = responseData?.recordings?.recording
         ?? responseData?.recordings
         ?? responseData?.recording
         ?? responseData?.data
         ?? (Array.isArray(responseData) || responseData?.recordID ? responseData : []);
      const recordingList = Array.isArray(rawRecordings) ? rawRecordings : rawRecordings ? [rawRecordings] : [];

      return recordingList.map((recording) => ({
         recordID: recording.recordID,
         meetingID: recording.meetingID,
         name: recording.name,
         published: recording.published,
         state: recording.state,
         startTime: recording.startTime,
         endTime: recording.endTime,
         playbackUrl: getPlaybackUrl(recording),
      }));
   };

   const formatRecordingDate = (value) => {
      if (!value) {
         return "-";
      }

      const date = new Date(Number(value));

      return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
   };

   const formatRecordingTime = (value) => {
      if (!value) {
         return "-";
      }

      const date = new Date(Number(value));

      return Number.isNaN(date.getTime()) ? "-" : date.toLocaleTimeString();
   };

   const formatRecordingLength = (startTime, endTime) => {
      if (!startTime || !endTime) {
         return "-";
      }

      const start = Number(startTime);
      const end = Number(endTime);

      if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
         return "-";
      }

      const totalSeconds = Math.floor((end - start) / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (hours > 0) {
         return `${hours}h ${minutes}m ${seconds}s`;
      }

      if (minutes > 0) {
         return `${minutes}m ${seconds}s`;
      }

      return `${seconds}s`;
   };

   const filteredRecordings = useMemo(() => {
      if (!recordingDateFilter?.length || (!recordingDateFilter[0] && !recordingDateFilter[1])) {
         return recordings;
      }

      const [startDate, endDate] = recordingDateFilter;
      const startTime = startDate ? startDate.startOf("day").valueOf() : Number.NEGATIVE_INFINITY;
      const endTime = endDate ? endDate.endOf("day").valueOf() : Number.POSITIVE_INFINITY;

      return recordings.filter((recording) => {
         const recordingDate = new Date(Number(recording.startTime));

         if (Number.isNaN(recordingDate.getTime())) {
            return false;
         }

         const recordingTime = recordingDate.getTime();

         return recordingTime >= startTime && recordingTime <= endTime;
      });
   }, [recordings, recordingDateFilter]);

   const getRecordingsByClass = (gradeLevelId, sectionId) => {
      const url = `${process.env.REACT_APP_API_BIGBLUEBUTTON}/getrecordings/picker`;
      var subdomain = getSubdomain();

      return axios.get(url, {
         params: {
            grade_level_id: gradeLevelId,
            section_id: sectionId,
            subdomain,
         },
         headers: {},
      })
         .then((response) => normalizeRecordingsData(response.data));
   };

   const getMeetingStatusesByClass = (classLookups) => {
      const url = `${process.env.REACT_APP_API_BIGBLUEBUTTON}/ismeetingrunning/picker/batch`;

      return axios.post(url, {
         subdomain: getSubdomain(),
         rooms: classLookups.map((item) => ({
            gradeLevelId: item.gradeLevelId,
            sectionId: item.sectionId,
            gradeLevelName: item.gradeLevelName,
            sectionName: item.sectionName,
         })),
      }, { headers: {} }).then((response) => response?.data || []);
   };

   const getMeetingAttendeesByClass = (classLookups) => {
      const url = `${process.env.REACT_APP_API_BIGBLUEBUTTON}/getmeetinginfo/picker/batch`;

      return axios.post(url, {
         subdomain: getSubdomain(),
         rooms: classLookups.map((item) => ({
            gradeLevelId: item.gradeLevelId,
            sectionId: item.sectionId,
            gradeLevelName: item.gradeLevelName,
            sectionName: item.sectionName,
         })),
      }, { headers: {} }).then((response) => response?.data || []);
   };

   const loadMeetingStatusAvailability = (liveClasses) => {
      const classLookups = [];
      const seenKeys = new Set();

      (liveClasses || []).forEach((payload) => {
         const lookupKey = getLiveClassKey(payload);

         if (!lookupKey || seenKeys.has(lookupKey)) {
            return;
         }

         seenKeys.add(lookupKey);
         classLookups.push({
            lookupKey,
            gradeLevelId: getGradeLevelId(payload),
            sectionId: getSectionId(payload),
            gradeLevelName: payload.grade_level_name,
            sectionName: payload.section_name,
         });
      });

      if (classLookups.length === 0) {
         setMeetingStatusLookup({});
         setAttendeesLookup({});
         return Promise.resolve();
      }

      setMeetingStatusLookup(classLookups.reduce((lookup, item) => ({
         ...lookup,
         [item.lookupKey]: {
            loading: true,
            isRunning: false,
            meetingId: "",
         },
      }), {}));

      return getMeetingStatusesByClass(classLookups).then((responseStatuses) => {
         const meetingStatuses = classLookups.map((item) => {
            const status = responseStatuses.find((entry) =>
               `${entry?.gradeLevelId ?? entry?.GradeLevelId ?? entry?.grade_level_id}` === `${item.gradeLevelId}`
               && `${entry?.sectionId ?? entry?.SectionId ?? entry?.section_id}` === `${item.sectionId}`
            );

            return {
               lookupKey: item.lookupKey,
               loading: false,
               isRunning: status?.isRunning === true || status?.IsRunning === true || status?.isRunning === "true" || status?.IsRunning === "true",
               meetingId: status?.meetingId ?? status?.MeetingId ?? status?.meeting_id ?? "",
            };
         });

         setMeetingStatusLookup(meetingStatuses.reduce((lookup, status) => ({
            ...lookup,
            [status.lookupKey]: status,
         }), {}));
         return loadAttendeesAvailability((liveClasses || []).filter((payload) => {
            const liveClassKey = getLiveClassKey(payload);
            return meetingStatuses.some((status) => status.lookupKey === liveClassKey && status.isRunning);
         }));
      }).catch((error) => {
         console.log(error.message);
         setMeetingStatusLookup(classLookups.reduce((lookup, item) => ({
            ...lookup,
            [item.lookupKey]: {
               loading: false,
               isRunning: false,
               meetingId: "",
               hasError: true,
            },
         }), {}));
         setAttendeesLookup({});
      });
   };

   const loadAttendeesAvailability = (liveClasses) => {
      const classLookups = [];
      const seenKeys = new Set();

      (liveClasses || []).forEach((payload) => {
         const lookupKey = getLiveClassKey(payload);

         if (!lookupKey || seenKeys.has(lookupKey)) {
            return;
         }

         seenKeys.add(lookupKey);
         classLookups.push({
            lookupKey,
            gradeLevelId: getGradeLevelId(payload),
            sectionId: getSectionId(payload),
            gradeLevelName: payload.grade_level_name,
            sectionName: payload.section_name,
         });
      });

      if (classLookups.length === 0) {
         setAttendeesLookup({});
         return Promise.resolve();
      }

      setAttendeesLookup(classLookups.reduce((lookup, item) => ({
         ...lookup,
         [item.lookupKey]: {
            loading: true,
            moderatorCount: 0,
            viewerCount: 0,
         },
      }), {}));

      return getMeetingAttendeesByClass(classLookups).then((responseStatuses) => {
         const attendeesStatuses = classLookups.map((item) => {
            const status = responseStatuses.find((entry) =>
               `${entry?.gradeLevelId ?? entry?.GradeLevelId ?? entry?.grade_level_id}` === `${item.gradeLevelId}`
               && `${entry?.sectionId ?? entry?.SectionId ?? entry?.section_id}` === `${item.sectionId}`
            );

            return {
               lookupKey: item.lookupKey,
               loading: false,
               moderatorCount: Number(status?.moderatorCount ?? status?.ModeratorCount ?? status?.moderator_count ?? 0),
               viewerCount: Number(status?.viewerCount ?? status?.ViewerCount ?? status?.viewer_count ?? 0),
            };
         });

         setAttendeesLookup(attendeesStatuses.reduce((lookup, status) => ({
            ...lookup,
            [status.lookupKey]: status,
         }), {}));
      }).catch((error) => {
         console.log(error.message);
         setAttendeesLookup(classLookups.reduce((lookup, item) => ({
            ...lookup,
            [item.lookupKey]: {
               loading: false,
               moderatorCount: 0,
               viewerCount: 0,
               hasError: true,
            },
         }), {}));
      });
   };

   const getLiveClassPickerList = () => {
      if (pickerListRequestInFlight.current) {
         return;
      }

      pickerListRequestInFlight.current = true;
      setLoading(true);

      const url = `${process.env.REACT_APP_API_BIGBLUEBUTTON}/getpickerlist`;
      axios.get(url, { headers: {} }).then((response) => {
         const liveClasses = response.data || [];

         setLiveClassList(liveClasses);
         setLoading(false);
         setRecordingLookup({});
         setAttendeesLookup({});
         return loadMeetingStatusAvailability(liveClasses);
      }, (error) => {
         setLoading(false);
         setRecordingLookup({});
         setMeetingStatusLookup({});
         setAttendeesLookup({});
         console.log(error.message);
         Modal.error({
            title: "Brainee Live Class",
            content: "Unable to load live class rooms.",
            centered: true,
         });
      }).finally(() => {
         pickerListRequestInFlight.current = false;
      });
   };

   const refreshLiveClassPickerList = () => {
      if (pickerRefreshRequestInFlight.current) {
         return;
      }

      pickerRefreshRequestInFlight.current = true;
      setRefreshing(true);

      const url = `${process.env.REACT_APP_API_BIGBLUEBUTTON}/refreshpickerlist`;
      axios.post(url, null, { headers: {} }).then((response) => {
         setRefreshing(false);

         if (Array.isArray(response.data)) {
            setLiveClassList(response.data);
            setRecordingLookup({});
            setAttendeesLookup({});
            return loadMeetingStatusAvailability(response.data);
         } else {
            return getLiveClassPickerList();
         }
      }, (error) => {
         setRefreshing(false);
         console.log(error.message);
         Modal.error({
            title: "Brainee Live Class",
            content: "Unable to create picker list.",
            centered: true,
         });
      }).finally(() => {
         pickerRefreshRequestInFlight.current = false;
      });
   };

   const handleEnterClass = (payload) => {
      var subdomain = getSubdomain();

      const params = new URLSearchParams({
         subdomain,
         joiner_name: username,
         grade_level_id: getGradeLevelId(payload),
         section_id: getSectionId(payload),
         join_as: joinAs
      });

      const url = `${process.env.REACT_APP_API_BIGBLUEBUTTON}/joinclass?${params.toString()}`;
      window.open(url, "_blank", "noopener, noreferrer");
   };

   const handleShowRecordings = async (payload) => {
      const lookupKey = getRecordingLookupKey(payload);
      const recordingStatus = recordingLookup[lookupKey];

      if (!lookupKey || recordingStatus?.loading) {
         return;
      }

      setSelectedMeetingName(getRoomName(payload) || "Recordings");
      setRecordingDateFilter(null);
      setRecordingsModalOpen(true);

      if (Array.isArray(recordingStatus?.recordings)) {
         setRecordings(recordingStatus.recordings);
         setRecordingsLoading(false);
         return;
      }

      setRecordings([]);
      setRecordingsLoading(true);
      setRecordingLookup((current) => ({
         ...current,
         [lookupKey]: { loading: true, count: 0 },
      }));

      try {
         const recordingList = await getRecordingsByClass(getGradeLevelId(payload), getSectionId(payload));
         setRecordings(recordingList);
         setSelectedMeetingName(recordingList.find((recording) => recording.name)?.name || getRoomName(payload) || "Recordings");
         setRecordingLookup((current) => ({
            ...current,
            [lookupKey]: {
               loading: false,
               count: recordingList.length,
               recordings: recordingList,
            },
         }));
      } catch (error) {
         console.log(error.message);
         setRecordingLookup((current) => ({
            ...current,
            [lookupKey]: { loading: false, count: 0, recordings: [], hasError: true },
         }));
         Modal.error({
            title: "Brainee Live Class",
            content: "Unable to load recordings.",
            centered: true,
         });
      } finally {
         setRecordingsLoading(false);
      }
   };

   const handleEndMeeting = (payload, options = {}) => {
      const { force = false } = options;
      const gradeLevelId = getGradeLevelId(payload);
      const sectionId = getSectionId(payload);
      const liveClassKey = getLiveClassKey(payload);
      const meetingStatus = meetingStatusLookup[liveClassKey];

      if (!gradeLevelId || !sectionId) {
         Modal.error({
            title: "Brainee Live Class",
            content: "Unable to end meeting due to missing class details.",
            centered: true,
         });
         return;
      }

      if (!meetingStatus?.isRunning && !force) {
         Modal.info({
            title: "Brainee Live Class",
            content: "No ongoing meeting found for this room.",
            centered: true,
         });
         return;
      }

      setEndingMeetingKey(liveClassKey);

      const url = force
         ? `${process.env.REACT_APP_API_BIGBLUEBUTTON}/forceendmeeting/picker`
         : `${process.env.REACT_APP_API_BIGBLUEBUTTON}/endmeeting/picker`;
      axios.post(url, null, {
         params: {
            subdomain: getSubdomain(),
            grade_level_id: gradeLevelId,
            section_id: sectionId,
         },
         headers: {},
      }).then(() => {
         setEndingMeetingKey("");
         Modal.success({
            title: "Brainee Live Class",
            content: force ? "Meeting session force ended successfully." : "Meeting ended successfully.",
            centered: true,
         });
         getLiveClassPickerList();
      }, (error) => {
         setEndingMeetingKey("");
         console.log(error.message);
         Modal.error({
            title: "Brainee Live Class",
            content: "Unable to end meeting.",
            centered: true,
         });
      });
   };

   const handleCloseRecordingsModal = () => {
      setRecordingsModalOpen(false);
      setRecordings([]);
      setRecordingDateFilter(null);
      setSelectedMeetingName("");
   };

   const recordingsColumns = [
      {
         title: "Date",
         dataIndex: "startTime",
         key: "date",
         render: formatRecordingDate,
         width: "25%",
      },
      {
         title: "Time Started",
         dataIndex: "startTime",
         key: "timeStarted",
         render: formatRecordingTime,
         width: "25%",
      },
      {
         title: "Length",
         key: "length",
         render: (recording) => formatRecordingLength(recording.startTime, recording.endTime),
         width: "25%",
      },
      {
         title: "Action",
         key: "playback",
         align: "center",
         render: (recording) => (
            <Tooltip placement="left" title={recording.playbackUrl ? "Play Recording" : "Playback URL not available"}>
               <Button
                  type="primary"
                  size="small"
                  shape="round"
                  icon={<PlayCircleOutlined />}
                  disabled={!recording.playbackUrl}
                  onClick={() => window.open(recording.playbackUrl, "_blank", "noopener, noreferrer")}
               />
            </Tooltip>
         ),
         width: "25%",
      },
   ];

   const columns = [
      {
         title: "Room",
         key: "room",
         render: (payload) => getRoomName(payload),
         width: "55%",
      },
      {
         title: "Attendees",
         key: "attendees",
         align: "left",
         render: (payload) => {
            const liveClassKey = getLiveClassKey(payload);
            const attendeeStatus = attendeesLookup[liveClassKey];
            const meetingStatus = meetingStatusLookup[liveClassKey];
            const attendeesLoading = Boolean(meetingStatus?.loading || (meetingStatus?.isRunning && (!attendeeStatus || attendeeStatus.loading)));
            const moderatorCount = Number(attendeeStatus?.moderatorCount ?? 0);
            const viewerCount = Number(attendeeStatus?.viewerCount ?? 0);

            if (attendeesLoading) {
               return "Checking...";
            }

            return (
               <div>
                  <div>{`Teacher: ${Number.isFinite(moderatorCount) ? moderatorCount : 0}`}</div>
                  <div>{`Student: ${Number.isFinite(viewerCount) ? viewerCount : 0}`}</div>
               </div>
            );
         },
         width: "20%",
      },
      {
         title: "Action",
         key: "action",
         align: "center",
         render: (payload) => {
            const lookupKey = getRecordingLookupKey(payload);
            const liveClassKey = getLiveClassKey(payload);
            const recordingStatus = recordingLookup[lookupKey];
            const meetingStatus = meetingStatusLookup[liveClassKey];
            const recordingButtonLoading = Boolean(recordingStatus?.loading);
            const meetingStatusLoading = Boolean(liveClassKey && (!meetingStatus || meetingStatus.loading));
            const hasOngoingMeeting = Boolean(liveClassKey && meetingStatus?.isRunning);
            const endingMeeting = liveClassKey && endingMeetingKey === liveClassKey;
            const isForceEndIntent = Boolean(
               canManagePickerList
               && liveClassKey
               && hoveredMeetingKey === liveClassKey
               && isCtrlPressed
               && !hasOngoingMeeting
            );
            const canForceEndMeeting = Boolean(isForceEndIntent && !endingMeetingKey);
            const canEndMeeting = Boolean(hasOngoingMeeting || canForceEndMeeting);
            const recordingsTooltip = !lookupKey
               ? "No recordings available"
               : recordingButtonLoading
                   ? "Loading recordings"
                   : "View Recordings";
            const endMeetingTooltip = !liveClassKey
               ? "Meeting status unavailable"
               : isForceEndIntent
                  ? "Force End Meeting"
                  : meetingStatusLoading
                     ? "Checking meeting status"
                     : hasOngoingMeeting
                        ? "End Meeting"
                        : "Hold Ctrl while hovering this room to force end";
            const endMeetingPrompt = canForceEndMeeting
               ? "Force end this meeting session even if it is not marked as running?"
               : "Are you sure you want to end the meeting?";

            return (
               <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>                  
                  <Tooltip placement="left" title="Enter Room">
                     <Popconfirm
                        title="Are you sure you want to enter the class?"
                        onConfirm={() => handleEnterClass(payload)}
                        okText="Yes"
                        cancelText="No"
                     >
                        {/* <Button
                           type="primary"
                           size="small"
                           shape="circle"
                           icon={<LoginOutlined />}
                        /> */}
                        <Button
                           type={hasOngoingMeeting ? "default" : "primary"}
                           style={hasOngoingMeeting ? { backgroundColor: "#52c41a", borderColor: "#52c41a", color: "#fff" } : undefined}
                           size="small"
                           shape="circle"
                           icon={<LoginOutlined />}
                        />
                     </Popconfirm>
                  </Tooltip>
                  <Tooltip placement="top" title={recordingsTooltip}>
                     <span>
                        <Button
                           type="primary"
                           size="small"
                           shape="circle"
                           icon={<VideoCameraOutlined />}
                           loading={recordingButtonLoading}
                           disabled={!lookupKey}
                           onClick={() => handleShowRecordings(payload)}
                        />                        
                     </span>
                  </Tooltip>
                  {canManagePickerList && (
                     <Tooltip placement="top" title={endMeetingTooltip}>
                        <Popconfirm
                           title={endMeetingPrompt}
                           onConfirm={() => handleEndMeeting(payload, { force: canForceEndMeeting })}
                           okText="Yes"
                           cancelText="No"
                        >
                           <Button
                              danger
                              type="primary"
                              size="small"
                              shape="circle"
                              icon={<StopOutlined />}
                              loading={endingMeeting}
                              disabled={Boolean((endingMeetingKey && !endingMeeting) || !canEndMeeting)}
                           />
                        </Popconfirm>
                     </Tooltip>
                  )}
               </div>
            );
         },
         width: "25%",
      },
   ];

   return (
      <div style={{ textAlign: "center" }}>
         <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
            {/* <Tooltip placement="top" title="Refresh List"> */}
               <Button
                  shape="round"
                  type="default"
                  icon={<ReloadOutlined />}
                  loading={loading}
                  disabled={refreshing}
                  onClick={getLiveClassPickerList}
               >
                  Refresh
               </Button>
            {/* </Tooltip> */}
            {canManagePickerList && (
               <>
                  {liveClassList.length === 0 ? (
                     <Button
                        type="primary"
                        shape="round"
                        loading={refreshing}
                        onClick={refreshLiveClassPickerList}
                     >
                        Create List
                     </Button>
                  ) : (
                     <Popconfirm
                        title="All existing records will be cleared. Continue?"
                        onConfirm={refreshLiveClassPickerList}
                        okText="Yes"
                        cancelText="No"
                     >
                        <Button
                           type="primary"
                           shape="round"
                           loading={refreshing}
                        >
                           Re-create List
                        </Button>
                     </Popconfirm>
                  )}
               </>
            )}            
         </div>
         <Table
            rowKey="id"
            loading={loading}
            size="small"
            columns={columns}
            dataSource={liveClassList}
            onRow={(record) => {
               const liveClassKey = getLiveClassKey(record);

               return {
                  onMouseEnter: () => setHoveredMeetingKey(liveClassKey),
                  onMouseLeave: () => setHoveredMeetingKey((currentKey) => (currentKey === liveClassKey ? "" : currentKey)),
               };
            }}
            pagination={false}
            scroll={{
               y: "calc(100vh - 350px)",
               x: "450px",
            }}
         />
         <Modal
            title={selectedMeetingName || "Recordings"}
            open={recordingsModalOpen}
            onCancel={handleCloseRecordingsModal}
            footer={[
               <Button key="close" shape="round" onClick={handleCloseRecordingsModal}>
                  Close
               </Button>,
            ]}
            centered
            width={window.innerWidth < 768 ? "90%" : "35%"}
            style={{ minWidth: window.innerWidth < 768 ? "" : 450 }}
         >
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
               <RangePicker
                  allowClear
                  value={recordingDateFilter}
                  onChange={(dates) => setRecordingDateFilter(dates)}
                  format="MMM DD, YYYY"
                  placeholder={["From date", "To date"]}
               />
            </div>
            <Table
               rowKey={(recording) => recording.recordID || recording.playbackUrl || recording.startTime}
               loading={recordingsLoading}
               size="small"
               columns={recordingsColumns}
               dataSource={filteredRecordings}
               pagination={false}
               scroll={{
                  y: "calc(100vh - 420px)",
                  x: "350px",
               }}
            />
         </Modal>
      </div>
   );
}
